/**
 * DriverPickupMapDialog
 * ----------------------
 * Modal shown when the driver taps the pickup address on a booking card.
 * Displays:
 *   - Driver's current GPS position (blue dot) — obtained via browser Geolocation
 *   - Passenger pickup pin (yellow marker)
 *   - Route between the two (blue polyline via Google Directions)
 *   - ETA & distance
 *   - "Ouvrir dans Google Maps" and "Ouvrir dans Waze" deep-links
 *
 * Rendered ABOVE the booking flow so the driver reviews the itinerary BEFORE
 * heading over and typing the OTP at arrival.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { APIProvider, Map, Marker, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Dialog, DialogContent } from "../ui/dialog";
import { Button } from "../ui/button";
import { Navigation, Route, LocateFixed, Loader2, X, MapPin, Send, Check } from "lucide-react";
import api from "../../lib/api";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function DriverPickupMapDialog({
  open,
  onOpenChange,
  pickup,        // { lat, lng, address }
  passengerName,
  bookingLabel,
  bookingId,      // enables the "Je suis en route" push notification
}) {
  const [driverPos, setDriverPos] = useState(null);
  const [gpsErr, setGpsErr] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [routeMetrics, setRouteMetrics] = useState({ km: null, min: null });
  const [sendingOnMyWay, setSendingOnMyWay] = useState(false);
  const [onMyWaySent, setOnMyWaySent] = useState(false);
  const [onMyWayError, setOnMyWayError] = useState(null);
  const watchIdRef = useRef(null);

  // Start watching the driver's position while the dialog is open.
  useEffect(() => {
    if (!open) return;
    if (!("geolocation" in navigator)) { setGpsErr("GPS non supporté par cet appareil"); return; }
    setGpsLoading(true); setGpsErr(null);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setDriverPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsLoading(false);
      },
      (err) => {
        setGpsLoading(false);
        setGpsErr(err.code === 1 ? "Autorisation GPS refusée. Activez la localisation." : "Impossible d'obtenir votre position.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    };
  }, [open]);

  if (!pickup?.lat || !pickup?.lng) return null;

  const gmapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${pickup.lat},${pickup.lng}&travelmode=driving`;
  const wazeUrl = `https://waze.com/ul?ll=${pickup.lat},${pickup.lng}&navigate=yes`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="driver-pickup-map-dialog">
        <div className="flex items-center justify-between px-4 py-3 bg-[#0A2540] text-white">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-[#FACC15] font-bold">Aller chercher le passager</p>
            <p className="text-sm font-semibold truncate" data-testid="driver-pickup-dialog-title">{passengerName || bookingLabel || "Course en cours"}</p>
          </div>
          <button onClick={() => onOpenChange(false)} className="text-white/70 hover:text-white p-1" data-testid="driver-pickup-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="relative w-full" style={{ height: "60vh", minHeight: "360px" }}>
          {MAPS_KEY ? (
            <APIProvider apiKey={MAPS_KEY}>
              <PickupMap driverPos={driverPos} pickup={pickup} />
            </APIProvider>
          ) : (
            <div className="h-full flex items-center justify-center bg-[#F8FAFC] text-xs text-[#64748B]">Carte non disponible</div>
          )}
          {gpsLoading && (
            <div className="absolute top-3 left-3 bg-white rounded-full px-3 py-1.5 shadow border border-[#CBD5E1] text-xs text-[#0A2540] flex items-center gap-1.5" data-testid="driver-pickup-gps-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Recherche de votre position…
            </div>
          )}
          {gpsErr && (
            <div className="absolute top-3 left-3 right-3 bg-[#EF4444]/90 text-white rounded-lg px-3 py-2 text-xs" data-testid="driver-pickup-gps-error">
              {gpsErr}
            </div>
          )}
        </div>
        <div className="px-4 py-3 space-y-2 bg-white">
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="w-3.5 h-3.5 text-[#FACC15] mt-1 shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold">Adresse de prise en charge</p>
              <p className="text-[#0A2540] font-semibold" data-testid="driver-pickup-address-label">{pickup.address || "Lieu indiqué par le passager"}</p>
              <p className="text-[10px] text-[#94A3B8]">{pickup.lat.toFixed(5)}, {pickup.lng.toFixed(5)}</p>
            </div>
          </div>
          <RouteSummary driverPos={driverPos} pickup={pickup} onMetrics={(m) => setRouteMetrics(m)} />
          {bookingId && (
            <button
              type="button"
              onClick={async () => {
                if (sendingOnMyWay || onMyWaySent) return;
                setSendingOnMyWay(true); setOnMyWayError(null);
                try {
                  await api.post(`/bookings/${bookingId}/on-my-way`, {
                    eta_min: routeMetrics.min ? Math.round(routeMetrics.min) : null,
                    distance_km: routeMetrics.km ?? null,
                  });
                  setOnMyWaySent(true);
                  // Re-arm after 60s so the driver can send an updated ETA later.
                  setTimeout(() => setOnMyWaySent(false), 60_000);
                } catch (e) {
                  setOnMyWayError(e?.response?.data?.detail || "Erreur d'envoi");
                } finally { setSendingOnMyWay(false); }
              }}
              disabled={sendingOnMyWay || onMyWaySent}
              className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold transition ${onMyWaySent ? "bg-[#10B981] text-white" : "bg-[#FACC15] text-[#0A2540] hover:bg-[#FACC15]/85"} disabled:opacity-70`}
              data-testid="driver-on-my-way-btn"
            >
              {sendingOnMyWay ? <Loader2 className="w-4 h-4 animate-spin" />
                : onMyWaySent ? <><Check className="w-4 h-4" /> Notification envoyée au passager</>
                : <><Send className="w-4 h-4" /> Prévenir le passager que je suis en route
                    {routeMetrics.min ? ` (~${Math.round(routeMetrics.min)} min)` : ""}</>
              }
            </button>
          )}
          {onMyWayError && (
            <p className="text-[10px] text-[#EF4444] text-center" data-testid="driver-on-my-way-error">{onMyWayError}</p>
          )}
          <div className="flex gap-2 pt-1">
            <a
              href={gmapsUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl bg-[#0A2540] text-white text-xs font-bold hover:bg-[#0A2540]/90"
              data-testid="driver-pickup-open-gmaps"
            >
              <Navigation className="w-4 h-4" /> Google Maps
            </a>
            <a
              href={wazeUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl bg-[#33CCFF] text-white text-xs font-bold hover:bg-[#33CCFF]/90"
              data-testid="driver-pickup-open-waze"
            >
              <Navigation className="w-4 h-4" /> Waze
            </a>
          </div>
          <p className="text-[10px] text-center text-[#94A3B8] pt-1">Le code OTP se saisit à l'arrivée après avoir récupéré le passager.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RouteSummary({ driverPos, pickup, onMetrics }) {
  const routesLib = useMapsLibrary("routes");
  const [route, setRoute] = useState({ km: null, min: null, error: null, loading: false });
  useEffect(() => {
    if (!routesLib || !driverPos?.lat || !pickup?.lat) return;
    let cancelled = false;
    setRoute({ km: null, min: null, error: null, loading: true });
    const svc = new routesLib.DirectionsService();
    svc.route(
      {
        origin: { lat: driverPos.lat, lng: driverPos.lng },
        destination: { lat: pickup.lat, lng: pickup.lng },
        travelMode: routesLib.TravelMode.DRIVING,
      },
      (res, status) => {
        if (cancelled) return;
        if (status !== "OK" || !res?.routes?.[0]?.legs?.[0]) {
          setRoute({ km: null, min: null, error: "Trajet introuvable", loading: false });
          return;
        }
        const leg = res.routes[0].legs[0];
        const km = leg.distance.value / 1000;
        const min = leg.duration.value / 60;
        setRoute({ km, min, error: null, loading: false });
        onMetrics?.({ km, min });
      }
    );
    return () => { cancelled = true; };
  }, [routesLib, driverPos?.lat, driverPos?.lng, pickup?.lat, pickup?.lng, onMetrics]);

  if (!driverPos?.lat) return null;
  return (
    <div className="rounded-lg bg-[#F1F5F9] px-3 py-2 flex items-center gap-2" data-testid="driver-pickup-eta">
      <Route className="w-4 h-4 text-[#0A2540] shrink-0" />
      {route.loading ? (
        <span className="text-xs text-[#64748B] inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Calcul du trajet…</span>
      ) : route.error ? (
        <span className="text-xs text-[#EF4444]">{route.error}</span>
      ) : (
        <span className="text-xs font-semibold text-[#0A2540]">
          Distance ~{route.km?.toFixed(1)} km · Arrivée dans ~{Math.round(route.min || 0)} min
        </span>
      )}
    </div>
  );
}

function PickupMap({ driverPos, pickup }) {
  const map = useMap();
  const routesLib = useMapsLibrary("routes");
  const rendererRef = useRef(null);

  // Draw the route between driver and pickup
  useEffect(() => {
    if (!map || !routesLib || !driverPos?.lat || !pickup?.lat) return;
    if (!rendererRef.current) {
      rendererRef.current = new routesLib.DirectionsRenderer({
        map,
        suppressMarkers: true,
        polylineOptions: { strokeColor: "#0A2540", strokeWeight: 5, strokeOpacity: 0.7 },
      });
    }
    const svc = new routesLib.DirectionsService();
    svc.route(
      {
        origin: { lat: driverPos.lat, lng: driverPos.lng },
        destination: { lat: pickup.lat, lng: pickup.lng },
        travelMode: routesLib.TravelMode.DRIVING,
      },
      (res, status) => {
        if (status === "OK" && rendererRef.current) rendererRef.current.setDirections(res);
      }
    );
    return () => {
      if (rendererRef.current) { rendererRef.current.setMap(null); rendererRef.current = null; }
    };
  }, [map, routesLib, driverPos?.lat, driverPos?.lng, pickup?.lat, pickup?.lng]);

  // Fit bounds once both points are known
  useEffect(() => {
    if (!map || !driverPos?.lat || !pickup?.lat) return;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(driverPos);
    bounds.extend(pickup);
    map.fitBounds(bounds, 80);
  }, [map, driverPos?.lat, driverPos?.lng, pickup?.lat, pickup?.lng]);

  const center = driverPos?.lat ? driverPos : pickup;

  const pickupIcon = useMemo(() => ({
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="42" height="52" viewBox="0 0 42 52"><path d="M21 2 C11 2 3 10 3 20 c0 12 18 28 18 28 s18-16 18-28 c0-10-8-18-18-18z" fill="#FACC15" stroke="#0A2540" stroke-width="2.5"/><circle cx="21" cy="20" r="7" fill="#0A2540"/></svg>'),
    scaledSize: { width: 38, height: 48 },
    anchor: { x: 19, y: 46 },
  }), []);
  const driverIcon = useMemo(() => ({
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30"><circle cx="15" cy="15" r="14" fill="rgba(37,99,235,0.15)"/><circle cx="15" cy="15" r="7" fill="#2563EB" stroke="white" stroke-width="3"/></svg>'),
    scaledSize: { width: 30, height: 30 },
    anchor: { x: 15, y: 15 },
  }), []);

  return (
    <Map
      defaultCenter={center}
      defaultZoom={14}
      mapId="trigotour-driver-pickup"
      style={{ width: "100%", height: "100%" }}
      gestureHandling="greedy"
      disableDefaultUI={false}
      zoomControl={true}
      mapTypeControl={false}
      streetViewControl={false}
      fullscreenControl={false}
    >
      <Marker position={pickup} icon={pickupIcon} title={pickup.address || "Passager"} />
      {driverPos?.lat && <Marker position={driverPos} icon={driverIcon} title="Ma position" />}
    </Map>
  );
}
